import React from "react";
import { useAppSelector } from "../../hooks";
import * as R from "ramda"
import { ModemFinalKind, ModemNotRunReason, NQValueKind } from "@visonum/network-quality-sdk";
import ModemDiagram from "./ModemDiagram";
import { AppAssertionError } from "../../features/errors/errorSlice";
import styled from "@emotion/styled";
import ButtonContainer from "./ButtonContainer";
import t from "../../helper/t";
import SmartLink from "../elements/SmartLink";
import { nullOrValue } from "../../helper/utils";
import { limitSpeed } from "../../helper/utils/limitSpeed";
import { Phase } from "../../features/measurement/types";

interface ModemViewProps { }

const ModemView: React.FC<ModemViewProps> = () => {
  const state = useAppSelector(state => state.measurement);

  if (state.kind != Phase.ModemExecuting &&
    state.kind != Phase.CompletingWithModem &&
    state.kind != Phase.FinishedWithModem) {
    throw new AppAssertionError({ actual: state.kind, expected: "one of ModemExecuting CompletingWithModem FinishedWithModem" })
  }

  switch (state.modem.kind) {
    case NQValueKind.ModemIntermediateExecuting:
      return (
        <ModemTemplate>
          <ModemDiagram
            downloadSpeed={null}
            modemSpeed={null}
            bookedSpeedMax={null}
            bookedSpeedAvg={null}
            bookedSpeedMin={null}
            errorMessage={null} />
        </ModemTemplate>
      );

    case NQValueKind.ModemFinal:
      const modemFinal = state.modem.modemFinal;
      const initModem = state.prepareResult.init.modem;
      switch (modemFinal.kind) {
        case ModemFinalKind.ModemFinalAvailable:
          const provisionedDownloadSpeed = nullOrValue(initModem?.provisionedDownloadSpeed);
          return (
            <ModemTemplate>
              <ModemDiagram
                downloadSpeed={state.download.speed}
                modemSpeed={limitSpeed(modemFinal.speed, provisionedDownloadSpeed)}
                bookedSpeedMax={nullOrValue(initModem?.bookedDownloadSpeedMax)}
                bookedSpeedAvg={nullOrValue(initModem?.bookedDownloadSpeedAvg)}
                bookedSpeedMin={nullOrValue(initModem?.bookedDownloadSpeedMin)}
                errorMessage={null} />
              <ResultButtons />
            </ModemTemplate>
          );
        case ModemFinalKind.ModemFinalDidNotRun:
          if (modemFinal.reason != ModemNotRunReason.NotAvailable) {
            throw new AppAssertionError({ actual: modemFinal.reason, expected: ModemNotRunReason.NotAvailable })
          }
          return (
            <ModemTemplate>
              <ModemDiagram
                downloadSpeed={state.download.speed}
                modemSpeed={null}
                bookedSpeedMax={nullOrValue(initModem?.bookedDownloadSpeedMax)}
                bookedSpeedAvg={nullOrValue(initModem?.bookedDownloadSpeedAvg)}
                bookedSpeedMin={nullOrValue(initModem?.bookedDownloadSpeedMin)}
                errorMessage={{
                  title: t("Messung nicht möglich"),
                  message: t("Das Angebot wird in Deinem Versorgungsbereich aktuell überarbeitet")
                }}
              />
              <ResultButtons />
            </ModemTemplate>
          );
        case ModemFinalKind.ModemFinalFailed:
          return (
            <ModemTemplate>
              <ModemDiagram
                downloadSpeed={state.download.speed}
                modemSpeed={null}
                bookedSpeedMax={nullOrValue(initModem?.bookedDownloadSpeedMax)}
                bookedSpeedAvg={nullOrValue(initModem?.bookedDownloadSpeedAvg)}
                bookedSpeedMin={nullOrValue(initModem?.bookedDownloadSpeedMin)}
                errorMessage={{
                  title: t("Fehler"),
                  message: t("Die Messung von unserem Server zu Deinem Router hat leider nicht geklappt. Versuch es bitte später nochmal.")
                }}
              />
              <ResultButtons />
            </ModemTemplate>
          );
      }
  }
}

export default ModemView;

const ResultButtons: React.FC<{}> = () => {
  return (
    <ButtonContainer>
      <SmartLink href="/tipps">
        <div className="button-rectangle-left rectangle-grey">
          <div data-cy="button-tips" className="button-rectangle-text white-label">
            {t("Tipps zur Optimierung")}
          </div>
        </div>
      </SmartLink>
      <SmartLink href="/result">
        <div className="button-rectangle-right">
          <div data-cy="button-details" className="button-rectangle-text">{t("Details anzeigen")}</div>
        </div>
      </SmartLink>
    </ButtonContainer>
  )
}

const ModemImgDiv = styled.div([
  {
    marginLeft: 8,
    marginRight: 8
  }
]);

const ModemTemplate: React.FC<{}> = ({ children }) => {
  return (
    <div className="modem-measuring-content">
      <h1 className="modem-measuring-content-title-lg">
        {t("Das Ergebnis weicht von Deinem Tarif ab.")}
      </h1>
      <h1 className="modem-measuring-content-title-sm">
        {t("Abweichung von Deinem Tarif")}
      </h1>
      <p className="info">{t("Die maximale Download-Geschwindigkeit wird an diesem Gerät nicht erreicht.")}</p>
      <ModemImgDiv>
        {children}
      </ModemImgDiv>
    </div>)
}